const MISSING_USER_MESSAGE =
  "No se encontró el usuario de operaciones. Vuelve a iniciar sesión.";

const DEFAULT_HTTP_ERROR =
  "No fue posible completar la reasignación. Intenta nuevamente.";

const paymentValueFormatter = new Intl.NumberFormat("es-US", {
  style: "currency",
  currency: "COP",
});

export const PAYMENT_RECORD_STATUS_LABELS = {
  APPLIED: "Aplicado",
  CANCELLED: "Cancelado",
  PENDING_APPLICATION: "Pendiente de aplicación",
  ADJUSTED: "Ajustado",
};

export const createEmptyReassignmentItem = () => ({
  paymentRecordOldId: "",
  originalPayerDocument: "",
  correctedPayerDocument: "",
  observation: "",
});

const trimValue = (value) => String(value ?? "").trim();

export const normalizePaymentRecordOldId = (oldId) => {
  const value = trimValue(oldId);

  if (!/^\d+$/.test(value)) {
    return value;
  }

  return value.replace(/^0+(?=\d)/, "");
};

export const isValidPaymentRecordOldId = (oldId) => {
  const value = trimValue(oldId);
  return /^\d+$/.test(value) && !/^0+$/.test(value);
};

export const getReassignmentItemErrors = (item) => {
  const errors = {};
  const originalPayerDocument = trimValue(item?.originalPayerDocument);
  const correctedPayerDocument = trimValue(item?.correctedPayerDocument);
  const observation = trimValue(item?.observation);

  if (!isValidPaymentRecordOldId(item?.paymentRecordOldId)) {
    errors.paymentRecordOldId = "Ingresa un número entero positivo.";
  }

  if (!originalPayerDocument) {
    errors.originalPayerDocument = "La cédula original es obligatoria.";
  }

  if (!correctedPayerDocument) {
    errors.correctedPayerDocument = "La cédula corregida es obligatoria.";
  } else if (
    originalPayerDocument &&
    originalPayerDocument === correctedPayerDocument
  ) {
    errors.correctedPayerDocument =
      "La cédula corregida debe ser distinta a la original.";
  }

  if (!observation) {
    errors.observation = "El motivo de la corrección es obligatorio.";
  }

  return errors;
};

export const isReassignmentItemValid = (item) =>
  Object.keys(getReassignmentItemErrors(item)).length === 0;

export const areReassignmentItemsValid = (items) =>
  Array.isArray(items) &&
  items.length > 0 &&
  items.every(isReassignmentItemValid);

export const buildReassignmentRequest = (item) => ({
  paymentRecordOldId: Number(
    normalizePaymentRecordOldId(item.paymentRecordOldId)
  ),
  originalPayerDocument: trimValue(item.originalPayerDocument),
  correctedPayerDocument: trimValue(item.correctedPayerDocument),
  observation: trimValue(item.observation),
});

export const buildReassignmentPayload = (items) => {
  if (!areReassignmentItemsValid(items)) {
    throw new Error("Completa todos los campos obligatorios antes de continuar.");
  }

  return items.map(buildReassignmentRequest);
};

export const asResultList = (data) => (Array.isArray(data) ? data : []);

export const areAllItemsEligible = (results) => {
  const list = asResultList(results);
  return list.length > 0 && list.every((item) => item?.eligible === true);
};

export const paymentRecordStatusLabel = (status) =>
  PAYMENT_RECORD_STATUS_LABELS[status] || status || "—";

export const formatPaymentValue = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return "—";
  }
  return paymentValueFormatter.format(amount);
};

export const formatPaymentDate = (dateToFormat) => {
  if (!dateToFormat) {
    return "—";
  }

  const justDate = dateToFormat.toString().split("T")[0].split("-");
  if (justDate.length !== 3) {
    return "Fecha inválida";
  }

  return `${justDate[2]}-${justDate[1]}-${justDate[0]}`;
};

export const getReassignmentHttpErrorMessage = (
  error,
  fallback = DEFAULT_HTTP_ERROR
) =>
  error?.response?.data?.description ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export const getExecutionResultTone = (result) => {
  if (!result?.success) {
    return "error";
  }

  if (result.newPaymentRecord?.status === "PENDING_APPLICATION") {
    return "warning";
  }

  return "success";
};

export const getExecutionResultMessage = (result) => {
  if (!result?.success) {
    return result?.errorMessage || "No se pudo reasignar este pago.";
  }

  const newRecord = result.newPaymentRecord;
  const newId = newRecord?.oldId ?? "—";

  if (newRecord?.status === "PENDING_APPLICATION") {
    return `Pago reasignado al registro #${newId}, pero no hay cuota pendiente. Requiere seguimiento.`;
  }

  return `Pago reasignado y aplicado. Nuevo registro #${newId}.`;
};

export const validatePaymentDocumentReassignment = async (httpClient, items) => {
  const payload = buildReassignmentPayload(items);
  const response = await httpClient.post(
    "/v1/payment-record/reassign-document/validate",
    payload
  );

  return asResultList(response.data);
};

export const reassignPaymentDocument = async (httpClient, userUuid, items) => {
  const operationsUserUuid = trimValue(userUuid);

  if (!operationsUserUuid) {
    throw new Error(MISSING_USER_MESSAGE);
  }

  const payload = buildReassignmentPayload(items);
  const response = await httpClient.post(
    `/v1/payment-record/reassign-document/${operationsUserUuid}`,
    payload
  );

  return asResultList(response.data);
};
